import React, { lazy } from 'react';
import { Navigate, Route, BrowserRouter as Router, Routes, matchRoutes, useLocation } from 'react-router-dom';

// TODO: custom branding
import '@everfi/reactifi/dist/styles.scss';

import { ErrorBoundary } from './lib/ErrorBoundary';

import Admin from './admin';
import Authenticated from './authentication/Authenticated';
import Callback from './authentication/Callback';
import Impersonate from './authentication/Impersonate';
import { TokenStore, UserTypeTokenContext } from './authentication/TokenStore';
import Cportal from './cportal';
import environment from './environment';
import Main from './layout/Main';
import Student from './student';
import Teacher from './teacher';
import NextContentApp from './unauthenticated/NextContentApp';
import { Search as SearchComponent } from './unauthenticated/Search';
import Unsubscribed from './unauthenticated/Unsubscribed';
import Pathways from './unauthenticated/Pathways';
import { locales } from './lib/i18n';
import TeacherRegistration from './teacher/Register';
import Ceos from './ceos';

const { HOMEROOM, ADMINIFI, NEXT } = environment;
const ContentPreview = lazy(() => import('./cportal/ContentPreview'));
const Interstitial = lazy(() => import('./cportal/Interstitial'));

const AuthenticationContext = ({ app, userType }) => {
  return (
    <UserTypeTokenContext userType={userType}>
      <Authenticated app={app} />
    </UserTypeTokenContext>
  );
};

function LocaleRoute() {
  const location = useLocation();
  // TODO: implement the real list of locales
  const localeRoutes = locales.map(locale => {
    return { path: `${locale}/*` };
  });
  const matchedRoutes = matchRoutes(localeRoutes, location);

  const defaultRoutes = (
    <Route>
      <Route element={<AuthenticationContext app={ADMINIFI} userType="Administrator" />}>
        <Route element={<Main sidebar={true} app={ADMINIFI} portal="fit" />}>
          <Route path={`admin/*`} element={<Admin />} />
        </Route>
      </Route>
      <Route element={<AuthenticationContext app={ADMINIFI} userType="User" />}>
        <Route element={<Main sidebar={false} app={ADMINIFI} portal="learner" systemNotifications={true} />}>
          <Route path={`:slug/student/*`} element={<Student />} />
        </Route>
        <Route element={<Main sidebar={false} hideImpersonation={true} app={ADMINIFI} portal="customer" />}>
          <Route path={`:slug/cportal/content_preview/*`} element={<ContentPreview />} />
        </Route>
        <Route element={<Main sidebar={false} hideImpersonation={true} app={ADMINIFI} portal="customer" />}>
          <Route path=":slug/ceos/*" element={<Ceos />} />
        </Route>
        <Route element={<Main sidebar={false} app={ADMINIFI} portal="customer" />}>
          <Route path={`:slug/cportal/interstitial_redirect/*`} element={<Interstitial />} />
        </Route>
        <Route element={<Main sidebar={true} app={ADMINIFI} portal="customer" systemNotifications={true} />}>
          <Route path={`:slug/*`} element={<Cportal />} />
        </Route>
      </Route>
      <Route element={<AuthenticationContext app={HOMEROOM} userType="Homeroom" />}>
        <Route element={<Main sidebar={true} app={HOMEROOM} portal="teacher" />}>
          <Route path={`teacher/*`} element={<Teacher />} />
        </Route>
        <Route element={<Main sidebar={false} app={HOMEROOM} portal="teacher" />}>
          <Route path={`teacher_registration/*`} element={<TeacherRegistration />} />
        </Route>
      </Route>
      <Route element={<AuthenticationContext app={NEXT} userType="User" />}>
        <Route element={<Main sidebar={false} portal="next_learner" />} path={`:slug/learner`}>
          <Route path={':program_slug'}>
            <Route path={'pathway/:playlist_slug'}>
              <Route path={`:content_id`} element={<NextContentApp />} />
              <Route path={''} element={<Pathways />} />
            </Route>
            <Route path={'search'} element={<SearchComponent />} />
          </Route>
        </Route>
      </Route>
      <Route path="unsubscribed" element={<Unsubscribed />} />
    </Route>
  );

  // TODO: possible routing for the various portals
  // <Route element={<Main sidebar={false} />} >
  //   <Route path={`:slug/compliance-student/*`} element={<ComplianceStudent />} />
  //   <Route path={`:slug/k12-student/*`} element={<K12Student />} />
  //   <Route path={`:slug/executive/*`} element={<Executive />} />
  // </Route>
  // <Route element={<Main sidebar={true} />} >
  //   <Route path={`admin/*`} element={<Admin />} />
  //   {/* <Route path={`:slug/cportal`} element={<Cportal />} /> */}
  //   {/* <Route path={`:slug/sponsor`} element={<Sponsor />} /> */}
  //   {/* <Route path={`:slug/district-admin`} element={<DistrictAdmin />} /> */}
  // </Route>

  return (
    <Routes>
      {matchedRoutes ? <Route path="/:locale/">{defaultRoutes}</Route> : defaultRoutes}
      <Route path="*" element={<Navigate to="admin" />} />
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <TokenStore>
        <ErrorBoundary>
          <Routes>
            <Route path="callback/*" element={<Callback app={ADMINIFI} />} />
            <Route path="callback-homeroom/*" element={<Callback app={HOMEROOM} fallbackUserType="Homeroom" />} />
            <Route path="callback-next/*" element={<Callback app={NEXT} fallbackUserType="Next" />} />
            <Route path="homeroom/impersonate" element={<Impersonate />} />
            <Route path="*" element={<LocaleRoute />} />
          </Routes>
        </ErrorBoundary>
      </TokenStore>
    </Router>
  );
}

export default App;
